/*
 * Copyright (c) 2026. Orion Analytics. All Rights Reserved
 */

import { useEffect, useState } from "react";

import {
    TrendingUp,
    TrendingDown,
    Activity
} from "lucide-react";

import {
    fetchScrollPrices
} from "../../api";

export default function RightSidebar() {

    const [stocks, setStocks] = useState([]);

    useEffect(() => {

        const loadData = async () => {

            try {

                const data = await fetchScrollPrices();

                /*
                 Sort by absolute % move
                */

                const sorted = [...data]
                    .sort(
                        (a, b) =>
                            Math.abs(b.change_percent) -
                            Math.abs(a.change_percent)
                    )
                    .slice(0, 6);

                setStocks(sorted);

            } catch (err) {

                console.error(
                    "Sidebar data error:",
                    err
                );
            }
        };

        loadData();

        const interval = setInterval(
            loadData,
            15000
        );

        return () => clearInterval(interval);

    }, []);

    /*
     Sentiment Calculation
    */

    const positiveCount =
        stocks.filter(
            s => s.change_percent >= 0
        ).length;

    const sentiment =
        stocks.length > 0
            ? Math.round(
                (positiveCount / stocks.length) * 100
            )
            : 0;

    const sentimentText =
        sentiment >= 70
            ? "Strong Bullish Momentum"
            : sentiment >= 50
                ? "Moderately Bullish"
                : sentiment >= 40
                    ? "Neutral Market Tone"
                    : "Bearish Momentum";

    const sentimentColor =
        sentiment >= 50
            ? "text-emerald-400"
            : "text-red-400";

    return (

        <aside className="
            hidden
            xl:block
            w-80
            border-l
            border-zinc-800
            bg-zinc-950
            p-6
            space-y-6
            text-zinc-100
        ">

            {/* Market Sentiment */}

            <div className="
                bg-zinc-900
                border
                border-zinc-800
                rounded-2xl
                p-6
                shadow-lg
            ">

                <div className="
                    flex
                    items-center
                    justify-between
                    mb-5
                ">

                    <h3 className="
                        text-lg
                        font-bold
                        text-white
                    ">
                        Market Sentiment
                    </h3>

                    <div className="
                        flex
                        items-center
                        gap-1
                        px-2
                        py-1
                        rounded-full
                        bg-emerald-500/10
                        text-emerald-400
                        text-xs
                        font-semibold
                    ">

                        <Activity size={12} />

                        LIVE

                    </div>

                </div>

                <div className={`
                    text-6xl
                    font-extrabold
                    leading-none
                    ${sentimentColor}
                `}>

                    {sentiment}

                </div>

                <div className="
                    mt-3
                    text-zinc-300
                    text-sm
                ">

                    {sentimentText}

                </div>

                {/* Progress Bar */}

                <div className="
                    w-full
                    h-2
                    bg-zinc-800
                    rounded-full
                    mt-5
                    overflow-hidden
                ">

                    <div
                        className={`
                            h-full
                            transition-all
                            duration-500
                            ${
                                sentiment >= 50
                                    ? "bg-gradient-to-r from-emerald-400 to-cyan-400"
                                    : "bg-gradient-to-r from-red-400 to-orange-400"
                            }
                        `}
                        style={{
                            width: `${sentiment}%`
                        }}
                    />

                </div>

            </div>

            {/* Trending Stocks */}

            <div className="
                bg-zinc-900
                border
                border-zinc-800
                rounded-2xl
                p-6
                shadow-lg
            ">

                <div className="
                    flex
                    items-center
                    justify-between
                    mb-5
                ">

                    <h3 className="
                        text-lg
                        font-bold
                        text-white
                    ">
                        Trending Stocks
                    </h3>

                    <span className="
                        text-xs
                        text-zinc-500
                    ">
                        Live Market
                    </span>

                </div>

                <div className="space-y-4">

                    {stocks.map((stock, idx) => {

                        const positive =
                            stock.change_percent >= 0;

                        return (

                            <div
                                key={idx}
                                className="
                                    flex
                                    items-center
                                    justify-between
                                    pb-3
                                    border-b
                                    border-zinc-800
                                    last:border-0
                                    last:pb-0
                                "
                            >

                                <div>

                                    <div className="
                                        text-white
                                        font-semibold
                                    ">

                                        {stock.symbol}

                                    </div>

                                    <div className="
                                        text-xs
                                        text-zinc-500
                                        mt-1
                                    ">

                                        ₹{stock.last_price}

                                    </div>

                                </div>

                                <div className={`
                                    flex
                                    items-center
                                    gap-1
                                    font-semibold
                                    ${
                                        positive
                                            ? "text-emerald-400"
                                            : "text-red-400"
                                    }
                                `}>

                                    {positive
                                        ? <TrendingUp size={16} />
                                        : <TrendingDown size={16} />
                                    }

                                    {positive ? "+" : ""}
                                    {stock.change_percent.toFixed(2)}%

                                </div>

                            </div>
                        );
                    })}

                </div>

            </div>

        </aside>
    );
}